import { autocompletion } from '@codemirror/autocomplete';
import { Language, StreamLanguage } from '@codemirror/language';
import { Extension } from '@codemirror/state';

import { EditorLanguageConfiguration } from './configuration.js';
import {
  autocompletionExtension,
  AutocompleteProvider,
} from '../auto-completion/autocompletion.extension.js';
import { listAutocompletionExtension } from '../auto-completion/list/list-autocompletion-extension.js';
import type { ListAutocompletionConfig } from '../auto-completion/list/list-autocompletion-extension.js';
import { IntlProps } from '../utils/render-node.js';

const emptyLanguage: Language = StreamLanguage.define({
  token: (stream) => {
    stream.skipToEnd();
    return null;
  },
});

/** Adds providers to language config. */
export function getLanguageExtensions(
  language: Language | undefined,
  providers: Array<AutocompleteProvider | ListAutocompletionConfig> | undefined,
  intlProps: IntlProps,
) {
  const configLanguage = language ?? emptyLanguage;

  return [
    configLanguage,
    ...getAutocompleteExtensions(providers || [], intlProps).map(
      (autocomplete) => configLanguage.data.of({ autocomplete }),
    ),
  ];
}

/**
 * @internal
 * Maps autocomplete provider to extensions.
 */
export function getAutocompleteExtensions(
  providers: Array<AutocompleteProvider | ListAutocompletionConfig>,
  intlProps: IntlProps,
) {
  return providers.map((p) =>
    typeof p === 'function'
      ? autocompletionExtension(p, intlProps)
      : listAutocompletionExtension(p, intlProps),
  );
}

/** Gets extensions for language config. */
export function getLanguageConfiguration(
  languageConfig: EditorLanguageConfiguration,
  intlProps: IntlProps,
): Extension[] {
  let providers:
    | (AutocompleteProvider | ListAutocompletionConfig)[]
    | undefined;
  if (languageConfig.autocompleteOptions) {
    providers = languageConfig.autocompleteOptions.providers;
  }

  return [
    getLanguageExtensions(languageConfig.language, providers, intlProps),
    autocompletion({
      activateOnTyping: languageConfig.autocompleteOptions?.activateOnTyping,
      optionClass: languageConfig.autocompleteOptions?.optionClass,
    }),
    ...(languageConfig.extendWith ?? []),
  ];
}
